import React, { useState, useEffect, useContext, useCallback } from 'react';
import { UserContext } from './App';
import { authFetch, fetchCsrfToken } from './authFetch';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function asString(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  try { return JSON.stringify(val); } catch { return String(val); }
}

// =====================================================
// Progress Bar (same pattern as AdminImports)
// =====================================================
function CommissionProgressBar({ batchId, onComplete, onError }) {
  const [progress, setProgress] = useState({
    status: 'starting',
    processed_rows: 0,
    total_rows: 0,
    created_count: 0,
    skipped_count: 0,
    error_count: 0,
    current_step: 'Initializing...',
    progress_percentage: 0,
  });

  useEffect(() => {
    if (!batchId) return;

    const pollProgress = async () => {
      try {
        const res = await authFetch(`${BASE_URL}/api/commissions/progress/${batchId}/`);
        if (res.ok) {
          const data = await res.json();
          setProgress(data);
          if (data.status === 'completed') { onComplete(data); return; }
          if (data.status === 'error') { onError(data.error_message || 'Import failed'); return; }
        }
      } catch (e) {
        console.error('Error polling commission progress:', e);
      }
    };

    pollProgress();
    const interval = setInterval(() => {
      if (progress.status === 'completed' || progress.status === 'error') {
        clearInterval(interval);
        return;
      }
      pollProgress();
    }, 2000);

    return () => clearInterval(interval);
  }, [batchId, onComplete, onError]);

  return (
    <div className="bg-white border rounded-lg p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Import Progress</h3>
        <span className="text-sm text-gray-500">Batch ID: {batchId}</span>
      </div>
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>{progress.current_step}</span>
          <span>{progress.progress_percentage || 0}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all duration-300 ${
              progress.status === 'completed' ? 'bg-green-500' :
              progress.status === 'error' ? 'bg-red-500' : 'bg-blue-500'
            }`}
            style={{ width: `${progress.progress_percentage || 0}%` }}
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div className="bg-green-50 p-3 rounded">
          <div className="font-semibold text-green-800">Created</div>
          <div className="text-xl text-green-600">{progress.created_count || 0}</div>
        </div>
        <div className="bg-gray-50 p-3 rounded">
          <div className="font-semibold text-gray-800">Skipped</div>
          <div className="text-xl text-gray-600">{progress.skipped_count || 0}</div>
        </div>
        <div className="bg-red-50 p-3 rounded">
          <div className="font-semibold text-red-800">Errors</div>
          <div className="text-xl text-red-600">{progress.error_count || 0}</div>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// Tab 1: Import Commissions
// =====================================================
function ImportTab() {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [importProgress, setImportProgress] = useState(null);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [forceReimport, setForceReimport] = useState(false);

  const disabled = !file || processing;

  async function onDryRun() {
    if (!file) return;
    setError(''); setMessage(''); setImportProgress(null); setDryRun(null);
    setProcessing(true);
    try {
      await fetchCsrfToken();
      const form = new FormData();
      form.append('file', file);
      const res = await authFetch(`${BASE_URL}/api/commissions/dry-run/`, { method: 'POST', body: form });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setDryRun(data);
      setMessage('Dry-run complete. Review the preview below.');
    } catch (e) {
      setError(asString(e?.message || e));
    } finally {
      setProcessing(false);
    }
  }

  async function onCommit() {
    if (!file) return;
    setError(''); setMessage(''); setImportProgress(null);
    setProcessing(true);
    try {
      await fetchCsrfToken();
      const form = new FormData();
      form.append('file', file);
      if (forceReimport) form.append('force_reimport', 'true');
      const res = await authFetch(`${BASE_URL}/api/commissions/commit/`, { method: 'POST', body: form });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Processing failed (${res.status})`);
      }
      const result = await res.json();
      setCurrentBatchId(result.batch_id);
      setImportProgress({ status: 'processing', total_rows: result.total_rows, batch_id: result.batch_id });
      setMessage(`Import started! Tracking ${result.total_rows} rows (${result.import_type})...`);
    } catch (e) {
      setError(asString(e?.message || e));
      setProcessing(false);
    }
  }

  const handleComplete = useCallback((data) => {
    setProcessing(false);
    setMessage(
      `Import completed!\nCreated: ${data.created_count || 0}\nSkipped: ${data.skipped_count || 0}\nErrors: ${data.error_count || 0}`
    );
    setFile(null); setDryRun(null); setImportProgress(null); setCurrentBatchId(null);
  }, []);

  const handleError = useCallback((msg) => {
    setProcessing(false);
    setError(`Import failed: ${msg}`);
    setImportProgress(null); setCurrentBatchId(null);
  }, []);

  const FORMAT_LABELS = {
    aca: 'Individual ACA',
    medicare_advantage: 'Medicare Advantage',
    medicare_supplement: 'Medicare Supplemental',
    payment_summary: 'Payment Summary',
  };

  return (
    <div className="space-y-4">
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
        <h3 className="font-semibold text-blue-800">Commission Import</h3>
        <ul className="text-sm text-blue-700 mt-2 space-y-1">
          <li>Upload BCBS commission CSV files (ACA, Medicare Advantage, or Medicare Supplemental)</li>
          <li>Format is auto-detected from column headers</li>
          <li>Agent codes are matched to CRM agents via the Agent Code Mapping tab</li>
          <li>Payment summary files (2ndHalfPay.csv) are also supported</li>
        </ul>
      </div>

      {importProgress && (
        <CommissionProgressBar batchId={currentBatchId} onComplete={handleComplete} onError={handleError} />
      )}

      <div className="border rounded-lg p-4 space-y-4 bg-white shadow">
        <div>
          <label className="block text-sm font-medium mb-1">CSV File</label>
          <input
            type="file" accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="w-full border rounded px-3 py-2"
            disabled={processing}
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox" id="forceReimportComm"
            checked={forceReimport}
            onChange={(e) => setForceReimport(e.target.checked)}
            className="w-4 h-4" disabled={processing}
          />
          <label htmlFor="forceReimportComm" className="text-sm text-gray-700">
            Force re-import (allow previously imported files)
          </label>
        </div>

        <div className="flex gap-3">
          <button onClick={onDryRun} disabled={disabled}
            className={`px-4 py-2 rounded text-white ${disabled ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {processing && !importProgress ? 'Analyzing...' : 'Dry Run Preview'}
          </button>
          <button onClick={onCommit} disabled={disabled || !dryRun}
            className={`px-4 py-2 rounded text-white ${(disabled || !dryRun) ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}>
            {processing && importProgress ? 'Processing...' : 'Import File'}
          </button>
        </div>

        {!importProgress && message && <div className="text-sm text-green-700 whitespace-pre-wrap bg-green-50 p-3 rounded border">{message}</div>}
        {error && <div className="text-sm text-red-600 whitespace-pre-wrap bg-red-50 p-3 rounded border">{error}</div>}
      </div>

      {dryRun && !importProgress && (
        <div className="space-y-4">
          <div className="border rounded-lg p-4 bg-white shadow">
            <h2 className="text-lg font-semibold mb-3">Import Preview</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-blue-50 p-3 rounded">
                <div className="font-semibold text-blue-800">Format Detected</div>
                <div className="text-lg text-blue-600">{FORMAT_LABELS[dryRun.import_type] || dryRun.import_type}</div>
              </div>
              <div className="bg-green-50 p-3 rounded">
                <div className="font-semibold text-green-800">Total Rows</div>
                <div className="text-xl text-green-600">{dryRun.total_rows}</div>
              </div>
              <div className="bg-purple-50 p-3 rounded">
                <div className="font-semibold text-purple-800">Agent Codes</div>
                <div className="text-xl text-purple-600">{(dryRun.agent_codes || []).length}</div>
              </div>
              <div className="bg-yellow-50 p-3 rounded">
                <div className="font-semibold text-yellow-800">Total Comp</div>
                <div className="text-xl text-yellow-600">${parseFloat(dryRun.total_comp || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </div>
            </div>

            {(dryRun.unmapped_agent_codes || []).length > 0 && (
              <div className="mt-4 bg-amber-50 p-3 rounded border border-amber-200">
                <div className="font-semibold text-amber-800">Unmapped Agent Codes ({dryRun.unmapped_agent_codes.length})</div>
                <div className="text-sm text-amber-700 mt-1">
                  These agent codes have no CRM mapping: {dryRun.unmapped_agent_codes.join(', ')}
                </div>
                <div className="text-xs text-amber-600 mt-1">
                  Go to the Agent Code Mapping tab to map these codes before importing.
                </div>
              </div>
            )}

            {dryRun.duplicate_file && (
              <div className="mt-4 bg-red-50 p-3 rounded border border-red-200">
                <div className="font-semibold text-red-800">Duplicate File Detected</div>
                <div className="text-sm text-red-700">This file has already been processed. Check "Force re-import" to import again.</div>
              </div>
            )}
          </div>

          {(dryRun.sample_rows || []).length > 0 && (
            <div className="border rounded-lg p-4 bg-white shadow">
              <h3 className="text-lg font-semibold mb-3">Sample Data (first {dryRun.sample_rows.length} rows)</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      {(dryRun.headers || []).slice(0, 8).map((h, i) => (
                        <th key={i} className="px-2 py-1 text-left font-medium text-gray-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {dryRun.sample_rows.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        {(dryRun.headers || []).slice(0, 8).map((h, i) => (
                          <td key={i} className="px-2 py-1">{(row[h] || '').substring(0, 30)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="border rounded-lg p-4 bg-white shadow">
            <h3 className="text-lg font-semibold mb-3">Columns Detected ({(dryRun.headers || []).length})</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
              {(dryRun.headers || []).map((col, i) => (
                <div key={i} className="bg-gray-50 p-2 rounded border">{col}</div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================
// Tab 2: Agent Code Mapping
// =====================================================
function AgentCodeTab() {
  const [mappings, setMappings] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState('');
  const [newProfileId, setNewProfileId] = useState('');
  const [newName, setNewName] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [mappingsRes, profilesRes] = await Promise.all([
        authFetch(`${BASE_URL}/api/commissions/agent-codes/`),
        authFetch(`${BASE_URL}/api/commissions/agent-profiles/`),
      ]);
      if (mappingsRes.ok) setMappings(await mappingsRes.json());
      if (profilesRes.ok) setProfiles(await profilesRes.json());
    } catch (e) {
      setError(asString(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function createMapping() {
    if (!newCode || !newProfileId) return;
    setError(''); setMessage('');
    try {
      await fetchCsrfToken();
      const res = await authFetch(`${BASE_URL}/api/commissions/agent-codes/create/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_code: newCode, agent_profile_id: newProfileId, agent_name: newName }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Error ${res.status}`);
      }
      setMessage(`Mapping created: ${newCode}`);
      setNewCode(''); setNewProfileId(''); setNewName('');
      fetchData();
    } catch (e) {
      setError(asString(e?.message || e));
    }
  }

  async function deleteMapping(id) {
    try {
      await fetchCsrfToken();
      await authFetch(`${BASE_URL}/api/commissions/agent-codes/${id}/delete/`, { method: 'DELETE' });
      fetchData();
    } catch (e) {
      setError(asString(e?.message || e));
    }
  }

  async function autoMap() {
    setError(''); setMessage('');
    try {
      await fetchCsrfToken();
      const res = await authFetch(`${BASE_URL}/api/commissions/agent-codes/auto-map/`, { method: 'POST' });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setMessage(`Auto-mapped ${data.mapped_count} agent codes.`);
      fetchData();
    } catch (e) {
      setError(asString(e?.message || e));
    }
  }

  if (loading) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Agent Code Mappings ({mappings.length})</h3>
        <button onClick={autoMap}
          className="px-4 py-2 rounded text-white bg-purple-600 hover:bg-purple-700">
          Auto-Map by Name
        </button>
      </div>

      {message && <div className="text-sm text-green-700 bg-green-50 p-3 rounded border">{message}</div>}
      {error && <div className="text-sm text-red-600 bg-red-50 p-3 rounded border">{error}</div>}

      {/* New mapping form */}
      <div className="border rounded-lg p-4 bg-white shadow">
        <h4 className="font-semibold mb-2">Add Mapping</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text" placeholder="Agent Code (e.g., 5323019)"
            value={newCode} onChange={(e) => setNewCode(e.target.value)}
            className="border rounded px-3 py-2"
          />
          <select value={newProfileId} onChange={(e) => setNewProfileId(e.target.value)}
            className="border rounded px-3 py-2">
            <option value="">Select Agent...</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>{p.full_name} {p.npn ? `(NPN: ${p.npn})` : ''}</option>
            ))}
          </select>
          <input
            type="text" placeholder="Agent Name (from CSV)"
            value={newName} onChange={(e) => setNewName(e.target.value)}
            className="border rounded px-3 py-2"
          />
          <button onClick={createMapping}
            disabled={!newCode || !newProfileId}
            className={`px-4 py-2 rounded text-white ${(!newCode || !newProfileId) ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
            Add
          </button>
        </div>
      </div>

      {/* Existing mappings */}
      <div className="border rounded-lg bg-white shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Agent Code</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">CRM Agent</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">CSV Name</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Created</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {mappings.map((m) => (
              <tr key={m.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-mono">{m.agent_code}</td>
                <td className="px-4 py-2">{m.agent_profile_name || '-'}</td>
                <td className="px-4 py-2 text-gray-500">{m.agent_name || '-'}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}</td>
                <td className="px-4 py-2">
                  <button onClick={() => deleteMapping(m.id)}
                    className="text-red-600 hover:text-red-800 text-xs">Delete</button>
                </td>
              </tr>
            ))}
            {mappings.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">No agent code mappings yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================
// Tab 3: Pay Periods
// =====================================================
function PayPeriodsTab() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', start_date: '', end_date: '' });
  const [error, setError] = useState('');

  const fetchPeriods = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${BASE_URL}/api/commissions/pay-periods/`);
      if (res.ok) setPeriods(await res.json());
    } catch (e) {
      setError(asString(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

  async function createPeriod() {
    setError('');
    try {
      await fetchCsrfToken();
      const res = await authFetch(`${BASE_URL}/api/commissions/pay-periods/create/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || JSON.stringify(data) || `Error ${res.status}`);
      }
      setShowForm(false);
      setFormData({ name: '', start_date: '', end_date: '' });
      fetchPeriods();
    } catch (e) {
      setError(asString(e?.message || e));
    }
  }

  async function updateStatus(id, newStatus) {
    try {
      await fetchCsrfToken();
      await authFetch(`${BASE_URL}/api/commissions/pay-periods/${id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      fetchPeriods();
    } catch (e) {
      setError(asString(e?.message || e));
    }
  }

  const STATUS_COLORS = {
    open: 'bg-green-100 text-green-800',
    closed: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-blue-100 text-blue-800',
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Pay Periods ({periods.length})</h3>
        <button onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 rounded text-white bg-blue-600 hover:bg-blue-700">
          {showForm ? 'Cancel' : 'New Pay Period'}
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 p-3 rounded border">{error}</div>}

      {showForm && (
        <div className="border rounded-lg p-4 bg-white shadow">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input type="text" placeholder="Name (e.g., Jan 2026 - 2nd Half)"
              value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="border rounded px-3 py-2" />
            <input type="date" value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              className="border rounded px-3 py-2" />
            <input type="date" value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              className="border rounded px-3 py-2" />
            <button onClick={createPeriod}
              disabled={!formData.name || !formData.start_date || !formData.end_date}
              className={`px-4 py-2 rounded text-white ${(!formData.name || !formData.start_date || !formData.end_date) ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}>
              Create
            </button>
          </div>
        </div>
      )}

      <div className="border rounded-lg bg-white shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Name</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Start</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">End</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Status</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {periods.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium">{p.name}</td>
                <td className="px-4 py-2">{p.start_date}</td>
                <td className="px-4 py-2">{p.end_date}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[p.status] || ''}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-2 flex gap-2">
                  {p.status === 'open' && (
                    <button onClick={() => updateStatus(p.id, 'closed')}
                      className="text-yellow-600 hover:text-yellow-800 text-xs">Close</button>
                  )}
                  {p.status === 'closed' && (
                    <>
                      <button onClick={() => updateStatus(p.id, 'paid')}
                        className="text-blue-600 hover:text-blue-800 text-xs">Mark Paid</button>
                      <button onClick={() => updateStatus(p.id, 'open')}
                        className="text-green-600 hover:text-green-800 text-xs">Reopen</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {periods.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">No pay periods yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================
// Tab 4: Commission Records
// =====================================================
function RecordsTab() {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ source_type: '', agent_code: '' });
  const [summary, setSummary] = useState([]);
  const [showSummary, setShowSummary] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: '50' });
      if (filters.source_type) params.set('source_type', filters.source_type);
      if (filters.agent_code) params.set('agent_code', filters.agent_code);
      const res = await authFetch(`${BASE_URL}/api/commissions/records/?${params}`);
      if (res.ok) {
        const data = await res.json();
        setRecords(data.results || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Error fetching records:', e);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.source_type) params.set('source_type', filters.source_type);
      const res = await authFetch(`${BASE_URL}/api/commissions/summary/?${params}`);
      if (res.ok) setSummary(await res.json());
    } catch (e) {
      console.error('Error fetching summary:', e);
    }
  }, [filters.source_type]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);
  useEffect(() => { if (showSummary) fetchSummary(); }, [showSummary, fetchSummary]);

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Commission Records ({total})</h3>
        <button onClick={() => setShowSummary(!showSummary)}
          className="px-4 py-2 rounded text-white bg-purple-600 hover:bg-purple-700">
          {showSummary ? 'Hide Summary' : 'Show Agent Summary'}
        </button>
      </div>

      {/* Filters */}
      <div className="border rounded-lg p-4 bg-white shadow">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Source Type</label>
            <select value={filters.source_type}
              onChange={(e) => { setFilters({ ...filters, source_type: e.target.value }); setPage(1); }}
              className="w-full border rounded px-3 py-2 text-sm">
              <option value="">All Types</option>
              <option value="aca">Individual ACA</option>
              <option value="medicare_advantage">Medicare Advantage</option>
              <option value="medicare_supplement">Medicare Supplemental</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Agent Code</label>
            <input type="text" placeholder="Filter by agent code"
              value={filters.agent_code}
              onChange={(e) => { setFilters({ ...filters, agent_code: e.target.value }); setPage(1); }}
              className="w-full border rounded px-3 py-2 text-sm" />
          </div>
        </div>
      </div>

      {/* Agent Summary */}
      {showSummary && summary.length > 0 && (
        <div className="border rounded-lg p-4 bg-white shadow">
          <h4 className="font-semibold mb-2">Agent Commission Summary</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Agent Code</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Agent Name</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">CRM Agent</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Records</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Total Comp</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Total Flat Rate</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Total Admin Fee</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {summary.map((s, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono">{s.agent_code}</td>
                    <td className="px-4 py-2">{s.agent_name || '-'}</td>
                    <td className="px-4 py-2">{s.agent_profile_name || <span className="text-amber-600">Unmapped</span>}</td>
                    <td className="px-4 py-2 text-right">{s.record_count}</td>
                    <td className="px-4 py-2 text-right font-medium">${parseFloat(s.total_comp || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-2 text-right">${parseFloat(s.total_flat_rate || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-2 text-right">${parseFloat(s.total_admin_fee || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Records table */}
      {loading ? (
        <div className="text-gray-500">Loading records...</div>
      ) : (
        <div className="border rounded-lg bg-white shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Agent</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Subscriber</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Product</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">Comp</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">Flat Rate</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">Admin Fee</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Type</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {records.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.agent_profile_name || r.agent_name || '-'}</div>
                      <div className="text-xs text-gray-400 font-mono">{r.agent_code}</div>
                    </td>
                    <td className="px-3 py-2">{r.subscriber_name || '-'}</td>
                    <td className="px-3 py-2">{r.product_type || '-'}</td>
                    <td className="px-3 py-2 text-right font-medium">${parseFloat(r.comp_amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">${parseFloat(r.flat_rate || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">${parseFloat(r.admin_fee || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs">{r.source_type || '-'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{r.compensation_date || r.imported_at?.split('T')[0] || '-'}</td>
                  </tr>
                ))}
                {records.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-500">No commission records found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <span className="text-sm text-gray-500">Page {page} of {totalPages} ({total} records)</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
                  className={`px-3 py-1 rounded text-sm ${page <= 1 ? 'bg-gray-200 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                  Previous
                </button>
                <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
                  className={`px-3 py-1 rounded text-sm ${page >= totalPages ? 'bg-gray-200 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =====================================================
// Main Component
// =====================================================
export default function AdminCommissions() {
  const { currentUser } = useContext(UserContext) || { currentUser: null };
  const [activeTab, setActiveTab] = useState('import');

  const isAdmin =
    !!(currentUser?.is_staff) ||
    (currentUser?.groups || []).some((g) => (g?.name || '').toLowerCase() === 'admin');

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">Commissions</h1>
        <p className="mt-2 text-red-600">You don't have permission. Admin role required.</p>
      </div>
    );
  }

  const TABS = [
    { id: 'import', label: 'Import Commissions' },
    { id: 'mapping', label: 'Agent Code Mapping' },
    { id: 'periods', label: 'Pay Periods' },
    { id: 'records', label: 'Commission Records' },
  ];

  return (
    <div className="p-6 max-w-7xl">
      <h1 className="text-2xl font-semibold mb-4">Admin: Commission Management</h1>

      {/* Tab Navigation */}
      <div className="border-b mb-6">
        <div className="flex space-x-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'import' && <ImportTab />}
      {activeTab === 'mapping' && <AgentCodeTab />}
      {activeTab === 'periods' && <PayPeriodsTab />}
      {activeTab === 'records' && <RecordsTab />}
    </div>
  );
}
